import { convertToRaw, EditorState } from 'draft-js'
//
import dagre from 'dagre'
import { isNode } from 'react-flow-renderer'

import draftjsToMd from '../components/UI/RichTextArea/converters/draftJsToMd'
import _ from 'lodash'
import { textBaseFileExtensions } from '@/app.constant'
import dayjs from 'dayjs'

/**
 * Get agent ID from query params or localStorage
 * Priority: 1. URL query param 'agent', 2. localStorage 'selectedAgent', 3. null
 * @returns agent ID string or null if not found
 */
export const getAgentFromStorage = (): string | null => {
  const queryParams = new URLSearchParams(window.location.search)
  const agentId = queryParams.get('agent')
  if (agentId) {
    return agentId
  }

  const agentIdFromStorage = localStorage.getItem('selectedAgent')
  if (agentIdFromStorage) {
    return agentIdFromStorage
  }

  return null
}

const convertToMarkdownFormat = (editorData: any) => {
  const content = editorData.getCurrentContent()
  let rawContent = convertToRaw(content)
  let markup = draftjsToMd(rawContent, {})

  let splitMarkup = markup.replace('&nbsp;', ' ')

  let transformedMarkup = splitMarkup
    .replace(/(^\n+|\n+$)/g, '\n')
    .replace(/(^ +| +$)/g, '')

  return transformedMarkup
}

function formatURLs(text: string) {
  // Regular expression to detect URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const imageRegex = /(https?:\/\/[^\s]+?\.(jpeg|jpg|gif|png|webp|svg))/gi
  // Regular expression to detect if URL is already in Markdown format
  const markdownFormatRegex = /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g

  // Replace URLs that are not already in Markdown format
  return text.replace(urlRegex, (url: any) => {
    // If the whole text already contains the markdown formatted URL, skip the replacement
    const imageUrls = url.match(imageRegex) || ''
    if (imageUrls) {
      return `${imageUrls})`
    }

    if (markdownFormatRegex.test(text)) {
      return url
    }

    // Otherwise, format the URL
    return `[${url}](${url})`
  })
}

export const convertArrayToMarkdownFormat = (responses: any[]) => {
  return responses.map(response => {
    if (response.text instanceof EditorState) {
      response.text = convertToMarkdownFormat(response.text)
      response.text = formatURLs(response.text)
      return response
    } else {
      response.text = formatURLs(response.text)
      return response
    }
  })
}

export const convertDataToMarkdownFormat = (response: any) => {
  if (response instanceof EditorState) {
    return convertToMarkdownFormat(response)
  } else {
    return response
  }
}

export const getLayoutedElements = (elements: any, direction = 'TB') => {
  const nodeWidth = 150
  const nodeHeight = 100
  const left: any = 'left'
  const right: any = 'right'
  const top: any = 'top'
  const bottom: any = 'bottom'

  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))

  const isHorizontal = direction === 'LR'
  dagreGraph.setGraph({ rankdir: direction })

  elements.forEach((el: any) => {
    if (isNode(el)) {
      dagreGraph.setNode(el.id, { width: nodeWidth, height: nodeHeight })
    } else {
      dagreGraph.setEdge(el.source, el.target)
    }
  })

  dagre.layout(dagreGraph)

  return elements.map((el: any) => {
    if (isNode(el)) {
      const nodeWithPosition = dagreGraph.node(el.id)
      el.targetPosition = isHorizontal ? left : top
      el.sourcePosition = isHorizontal ? right : bottom

      // unfortunately we need this little hack to pass a slightly different position
      // to notify react flow about the change. Moreover we are shifting the dagre node position
      // (anchor=center center) to the top left so it matches the react flow node anchor point (top left).
      el.position = {
        x: nodeWithPosition.x - nodeWidth / 2 + Math.random() / 1000,
        y: nodeWithPosition.y - nodeHeight / 2,
      }
    }

    return el
  })
}

export const getChildElementsBasedOnElement = async (
  elements: any,
  element: any,
  childrens: any[],
  adHiddenValue: boolean = false,
) => {
  // Element Id is node ID which is used to check if parent is available for that particular id or not. By parent means child for example.
  // { id: 'a' }, { id: 'b', parent: 'a'}
  // In above object we are targeting a as element if we got A in any object then we have to toggle hidden ( true <> false )
  const updatedElements = [...elements]
  const parentElementHeirarchy = updatedElements.filter(
    (el: any) => el.parent === element.id,
  )
  const parentElementConnection = updatedElements.filter(
    (el: any) => el.source === element.id,
  )

  if (parentElementHeirarchy.length) {
    // We will call this function recursively until we reach last object / child on the basis of parent.
    if (parentElementConnection && parentElementConnection.length) {
      parentElementConnection.forEach((d: any) => {
        const obj = {
          ...d,
          isHidden: adHiddenValue ? adHiddenValue : d.isHidden,
        }
        childrens.push(obj)
      })
    }

    parentElementHeirarchy.forEach(async (d: any) => {
      const child = elements.find((e: any) => e?.parent === d?.id)
      if (adHiddenValue && d.isHidden && child && d.hasActiveToggle) {
        childrens.push({
          ...d,
        })
        return
      }
      const obj = {
        ...d,
        isHidden: adHiddenValue ? adHiddenValue : d.isHidden,
      }
      childrens.push(obj)
      await getChildElementsBasedOnElement(
        elements,
        d,
        childrens,
        adHiddenValue,
      )
    })
  }
  return childrens
}

export const ValidateUrl = (value: any) => {
  const index =
    /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
      value,
    )
  return index
}

export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') return ''
  const trimmed = url.trim()

  if (!/^https?:\/\//i.test(trimmed)) return ''

  const u = new URL(trimmed)
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return ''

  const params = new URLSearchParams(u.search)
  for (const [k, v] of params.entries()) {
    params.set(k, encodeURIComponent(v))
  }

  return `${u.origin}${u.pathname}${params.toString() ? '?' + params.toString() : ''}${u.hash}`
}

export const openNotificationLink = (link: string): void => {
  if (!link?.trim()) return

  const sanitizedUrl = sanitizeUrl(link)
  if (!sanitizedUrl) return

  const windowObj: any = window

  const needsRedirect =
    sanitizedUrl.includes(windowObj.OLD_PORTAL_URI) ||
    sanitizedUrl.includes('portal.noodlefactory.ai/quiz/') ||
    sanitizedUrl.includes('newportal.staging.noodlefactory.ai/quiz/') ||
    sanitizedUrl.includes('portal.staging.noodlefactory.ai/review-submission/')

  const finalUrl = needsRedirect
    ? windowObj.NEW_PORTAL_URI + sanitizedUrl.split('.ai/').pop()
    : sanitizedUrl

  window.open(finalUrl, '_blank')
}

export const serialize = function (obj: any) {
  var str = []
  const newObj = { ...obj }
  delete newObj.dateParameters
  for (var p in newObj) {
    if (obj.hasOwnProperty(p)) {
      // str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
      if (p.includes('filter') && obj[p]) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
      } else {
        if (obj[p]) {
          str.push(
            encodeURIComponent('filter_' + p.toString()) +
              '=' +
              encodeURIComponent(obj[p]),
          )
        }
      }
    }
  }
  return str.join('&')
}

export const serializeFilter = function (obj: any) {
  var str = []
  const newObj = { ...obj }
  delete newObj.dateParameters
  for (var p in newObj) {
    if (obj.hasOwnProperty(p)) {
      if (obj[p]) {
        str.push(
          encodeURIComponent(p.toString()) + '=' + encodeURIComponent(obj[p]),
        )
      }
    }
  }
  return str.join('&')
}

export const base64MimeType = (encoded: any) => {
  var result = null

  if (typeof encoded !== 'string') {
    return result
  }

  var mime = encoded.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/)

  if (mime && mime.length) {
    result = mime[1]
  }

  return result
}

export const convertToBlob = (
  b64Data: any,
  contentType = '',
  sliceSize = 512,
) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })
  return blob
}

export const getDateOfISOWeek = (w: number, y: number) => {
  var simple = new Date(y, 0, 1 + (w - 1) * 7)
  var dow = simple.getDay()
  var ISOweekStart = simple
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
  return dayjs(ISOweekStart).format('YYYY-MM-DD')
}

export const getUrlParameter = (query: any) => {
  if (query) {
    let vars = query.split('&')
    let query_string: any = {}
    for (let i = 0; i < vars.length; i++) {
      let pair = vars[i].split('=')
      let key = decodeURIComponent(pair[0])
      let value = decodeURIComponent(pair[1])
      if (typeof query_string[key] === 'undefined') {
        query_string[key] = decodeURIComponent(value)
      } else if (typeof query_string[key] === 'string') {
        let arr = [query_string[key], decodeURIComponent(value)]
        query_string[key] = arr
      } else {
        query_string[key].push(decodeURIComponent(value))
      }
    }
    return query_string
  }
  return {}
}

export const transformNestedLevels = (list: any) => {
  let map: any = {}
  let node: any
  let roots = []
  let i: any

  for (i = 0; i < list.length; i += 1) {
    map[list[i]._id] = i // initialize the map
    list[i].children = [] // initialize the children
  }
  for (i = 0; i < list.length; i += 1) {
    node = list[i]
    if (node.parent_id && list[map[node.parent_id]]) {
      // if you have dangling branches check that map[node.parentId] exists
      list[map[node.parent_id]].children.push(node)
    } else {
      roots.push(node)
    }
  }
  return roots
}

export const transformNestedModules = (data: any) => {
  function addDepth(arr: any, depth = 0) {
    arr.forEach((obj: any) => {
      obj.depth = depth
      addDepth(obj.children, depth + 1)
    })
  }
  addDepth(data)

  let result: any = []

  const cb = (e: any) => {
    result.push({
      ...e,
      value: e._id,
      label: e.name,
      parent_id: e.parent_id,
      isIndented: e.parent_id ? true : false,
      depth: e.depth,
    })
    e.children && e.children.forEach(cb)
  }

  data.forEach(cb)
  return result
}

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const onAgentSearchHandler = (
  allAgents: any,
  query: string = '',
  agentOrder: any,
) => {
  let temp = [...allAgents]
  temp = _.orderBy(
    temp,
    agentOrder.value === null
      ? [(u: any) => u.name.toLowerCase()]
      : [(u: any) => new Date(u.updated_at)],
    'asc',
  )
  if (temp && temp.length > 0) {
    if (query && query.trim().length > 0) {
      temp = temp.filter((e: any) =>
        e.name.toLowerCase().includes(query.toLowerCase()),
      )
    }
  }
  return temp
}

export const getClassIdsFromConnectionIds = async (
  classes: any[],
  connection_id: string,
) => {
  let temp: any = {}
  let connectionIds = connection_id.split(',')
  let classIds: string[] = []
  classes.forEach((record: any) => {
    record.connections.forEach((connection: any) => {
      temp[connection.connection_id] = record._id
    })
  })
  if (connectionIds && connectionIds.length) {
    connectionIds.forEach((c: string) => {
      if (Object.keys(temp).includes(c)) {
        classIds.push(temp[c])
      }
    })
  }
  return _.uniq(classIds).join(',')
}

export const isTextBaseFile = (file_name: string) => {
  // use textBaseFileExtensions
  // to check if the file is a text-based file
  if (!file_name) return false
  file_name = file_name.toLowerCase()
  return textBaseFileExtensions.some((ext: string) => file_name.endsWith(ext))
}

export const isAllowedImageExtension = (fileName: string): boolean => {
  if (!fileName || typeof fileName !== 'string') return false
  const idx = fileName.lastIndexOf('.')
  if (idx < 0) return false
  const ext = fileName.slice(idx).toLowerCase()
  // xbm, tif, tiff, pjp, apng, jpeg, heif, ico, webp, svgz, jpg, heic, gif, svg, png, bmp, pjpeg, avif
  const allowedExtensions = [
    '.xbm',
    '.tif',
    '.tiff',
    '.pjp',
    '.apng',
    '.jpeg',
    '.heif',
    '.ico',
    '.webp',
    '.svgz',
    '.jpg',
    '.heic',
    '.gif',
    '.svg',
    '.png',
    '.bmp',
    '.pjpeg',
    '.avif',
  ]
  return allowedExtensions.includes(ext)
}

export const escapeHtml = (str: string) => {
  if (str === null || str === undefined) return ''
  return String(str)
    .replace(
      /[&<>"']/g,
      ch =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[ch],
    )
    .replace(/\n/g, '<br />')
}
